import type { ReactNode } from 'react'

const ONE_DAY_MS = 86_400_000
// ~2 months. At or below this the axis reads well with weekly ticks (≤ ~9
// labels); beyond it — e.g. a 3-month range's ~13 weekly labels — they crowd
// and overlap, so step up to monthly.
const WEEKLY_TICK_MAX_DAYS = 62

/**
 * One bar series — a labelled, coloured set of day-resolution counts. The
 * public shape is deliberately minimal (no channel/`MetricSeries` coupling) so
 * any consumer can feed it plain data.
 */
export interface BarChartSeries {
  /** Stable key; also the recharts `dataKey`. */
  key: string
  /** Legend / tooltip label. */
  label: string
  /** CSS colour; a `var(…)` reference is resolved for recharts. */
  color: string
  /** Optional tooltip icon (shown when `showIcons` is set). */
  icon?: ReactNode
  /** Day-resolution points; a null value renders as an absent (zero) bar. */
  data: { date: string; value: number | null }[]
}

/** One recharts row: a timestamp plus each series' value keyed by series key. */
export interface BarChartDataPoint {
  timestamp: number
  [key: string]: number
}

/**
 * Merge per-series day points into recharts rows keyed by UTC-day timestamp,
 * one column per series. A null (no reading) becomes 0 — a count metric has no
 * bar that day. Rows are ordered chronologically. Pure.
 */
export function buildBarChartData(
  series: BarChartSeries[],
): BarChartDataPoint[] {
  const timestampSet = new Set<number>()
  for (const s of series) {
    for (const point of s.data) {
      timestampSet.add(new Date(point.date).getTime())
    }
  }
  const timestamps = Array.from(timestampSet).toSorted((a, b) => a - b)

  const lookups = series.map((s) => {
    const lookup = new Map<number, number>()
    for (const point of s.data) {
      lookup.set(new Date(point.date).getTime(), point.value ?? 0)
    }
    return { key: s.key, lookup }
  })

  return timestamps.map((ts) => {
    const row: BarChartDataPoint = { timestamp: ts }
    for (const { key, lookup } of lookups) {
      row[key] = lookup.get(ts) ?? 0
    }
    return row
  })
}

export type BarTimeGranularity = 'day' | 'week' | 'month'

/**
 * Choose x-axis tick spacing from the visible date range so the axis stays
 * readable as the window widens: ≤ 7 days → per-day, up to ~2 months → weekly,
 * beyond that → monthly. Pure.
 */
export function pickBarTimeGranularity(
  startDate: Date,
  endDate: Date,
): BarTimeGranularity {
  const days =
    Math.round((endDate.getTime() - startDate.getTime()) / ONE_DAY_MS) + 1
  if (days <= 7) return 'day'
  if (days <= WEEKLY_TICK_MAX_DAYS) return 'week'
  return 'month'
}

/** Calendar months the range touches, inclusive (e.g. Mar–Jun = 4). Drives
 * whether monthly ticks have room for a full year or need the compact form. */
export function monthsInRange(startDate: Date, endDate: Date): number {
  return (
    (endDate.getFullYear() - startDate.getFullYear()) * 12 +
    (endDate.getMonth() - startDate.getMonth()) +
    1
  )
}

/**
 * Format a bucket's tooltip header from its start timestamp so it reads as the
 * period it represents rather than a single day:
 * - `day`   → `Jun 8, 2026`
 * - `week`  → `Jun 8 – Jun 14, 2026` (the bucket's Monday through Sunday)
 * - `month` → `June 2026`
 *
 * Dates render in UTC to match how points are stored. Pure.
 */
export function makeBucketTooltipFormatter(
  granularity: BarTimeGranularity,
): (timestampMs: number) => string {
  if (granularity === 'month') {
    return (ts) =>
      new Date(ts).toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      })
  }

  if (granularity === 'day') {
    return (ts) =>
      new Date(ts).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      })
  }

  // Week → the bucket's Monday through Sunday.
  return (ts) => {
    const to = ts + 6 * ONE_DAY_MS
    const spansYears =
      new Date(ts).getUTCFullYear() !== new Date(to).getUTCFullYear()
    const fromStr = new Date(ts).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      ...(spansYears ? { year: 'numeric' } : {}),
      timeZone: 'UTC',
    })
    const toStr = new Date(to).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    })
    return `${fromStr} – ${toStr}`
  }
}

/**
 * Y-axis domain `[0, max]` for the bars: the tallest per-period total when
 * stacked, or the tallest single column when grouped. Returns `[0, 'auto']`
 * when there's nothing to size against, and a fixed `[0, 5]` placeholder when
 * an empty state is showing so the axes still render. Pure.
 */
export function computeBarYDomain(
  data: BarChartDataPoint[],
  series: BarChartSeries[],
  stacked: boolean,
  hasEmptyState: boolean,
): [number, number | string] {
  if (hasEmptyState) return [0, 5]
  if (data.length === 0 || series.length === 0) return [0, 'auto']

  let max = 0
  for (const point of data) {
    if (stacked) {
      let sum = 0
      for (const s of series) sum += point[s.key] ?? 0
      max = Math.max(max, sum)
    } else {
      for (const s of series) max = Math.max(max, point[s.key] ?? 0)
    }
  }

  return max === 0 ? [0, 'auto'] : [0, max]
}
